import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { URLS } from '../../../constants/urls';
import { useMutation } from '@apollo/client';
import { GET_CAR, UPDATE_CAR_IMAGES, GET_PRESIGNED_URLS } from '../../services/carService/queries';
import { toast } from 'react-toastify';
import { Button } from '../button';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { Colors } from '../../styles/colors';
import { useCallback, useRef } from 'react';
import { Modal } from '../modal';

const Container = styled.div`
  padding: 8px;
`;

const PhotoList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;
`;

const PhotoItem = styled.div`
  position: relative;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  width: calc(50% - 8px);

  @media (min-width: 768px) {
    width: calc(33.333% - 11px);
  }

  @media (min-width: 1024px) {
    width: calc(25% - 12px);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const PhotoThumb = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
`;

const PhotoInfo = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${Colors.White};
`;

const MainBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: ${Colors.PrimaryIndigo};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  z-index: 1;
`;

const DeleteButton = styled.button`
  background: rgba(255, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;

  &:hover {
    background: #ff0000;
  }
`;

const PhotoLabel = styled.span`
  font-size: 14px;
  color: ${Colors.Gray600};
  font-weight: 500;
`;

const SaveButtonContainer = styled.div`
  margin-top: 40px;
  display: flex;
  gap: 16px;
`;

const AddPhotosCard = styled.button`
  aspect-ratio: 4 / 3;
  border: 2px dashed ${Colors.Gray300};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${Colors.Gray50};
  width: calc(50% - 8px);

  @media (min-width: 768px) {
    width: calc(33.333% - 11px);
  }

  @media (min-width: 1024px) {
    width: calc(25% - 12px);
  }

  &:hover {
    border-color: ${Colors.PrimaryIndigo};
    background-color: ${Colors.PrimaryIndigoLight}1a;
  }

  &:disabled {
    background-color: ${Colors.Gray400};
    cursor: not-allowed;
  }
`;

const PlusIcon = styled.div`
  font-size: 2.5rem;
  font-weight: 300;
  color: ${Colors.Gray400};
  line-height: 1;
  margin-bottom: 4px;

  ${AddPhotosCard}:hover & {
    color: ${Colors.PrimaryIndigo};
  }
`;

const AddText = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${Colors.Gray500};
  text-transform: uppercase;

  ${AddPhotosCard}:hover & {
    color: ${Colors.PrimaryIndigo};
  }
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 8px;
  background-color: ${Colors.Gray200};
  border-radius: 4px;
  margin-top: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background-color: ${Colors.PrimaryIndigo};
    transition: width 0.3s ease;
  }
`;

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 320px;
  max-width: 600px;
`;

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  border: 2px dashed ${props => (props.isDragActive ? Colors.PrimaryIndigo : Colors.Gray300)};
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  background: ${props => (props.isDragActive ? Colors.PrimaryIndigoLight + '1a' : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.PrimaryIndigo};
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
`;

const PreviewCard = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid ${Colors.Gray200};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${Colors.Gray100};
`;

interface EditCarPhotosProps {
  photos: string[];
  carId: string;
}

const StrictModeDroppable = ({ children, ...props }: any) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  const DroppableComponent = Droppable as any;
  return <DroppableComponent {...props}>{children}</DroppableComponent>;
};

const EditCarPhotos: React.FC<EditCarPhotosProps> = ({ photos: passedInPhotos, carId }) => {
  const [photos, setPhotos] = useState(passedInPhotos);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [uploadedKeys, setUploadedKeys] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({});
  const hasChangesRef = useRef(false);

  const [getPresignedUrls] = useMutation(GET_PRESIGNED_URLS);

  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 4,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg',
    };
    try {
      const compressedFile = await imageCompression(file, options);
      return new File([compressedFile], file.name, { type: 'image/jpeg' });
    } catch (error) {
      return file;
    }
  };

  const uploadFileToS3 = (
    file: File,
    index: number,
    s3Url: string,
    s3Key: string,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', s3Url);
      xhr.setRequestHeader('Content-Type', 'image/jpeg');
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(prev => ({ ...prev, [index]: percentComplete }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(s3Key);
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(file);
    });
  };

  const handleModalSave = async () => {
    if (selectedFiles.length === 0) {
      setIsModalOpen(false);
      return;
    }

    setIsUploading(true);
    try {
      const { data } = await getPresignedUrls({
        variables: {
          fileType: 'image/jpeg',
          carId,
          count: selectedFiles.length,
        },
      });

      const presignedData = data.getPresignedUrls;

      const keys = await Promise.all(
        selectedFiles.map(async (file, index) => {
          const compressedFile = await compressImage(file);
          const { url, key } = presignedData[index];
          return uploadFileToS3(compressedFile, index, url, key);
        }),
      );

      const finalPhotos = [...photos, ...keys];

      await updateCarImages({
        variables: {
          carId,
          updateImagesData: { imageUrls: finalPhotos },
        },
      });

      setPhotos(finalPhotos);
      toast.success(`Successfully added and saved ${keys.length} photos.`);
      handleCloseModal(true);
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  const handleCloseModal = async (force = false) => {
    if (!force && selectedFiles.length > 0) {
      if (window.confirm('Do you want to save changes?')) {
        await handleModalSave();
        return;
      }
    }
    setIsModalOpen(false);
    setSelectedFiles([]);
    setFilePreviews([]);
    setUploadedKeys([]);
    hasChangesRef.current = false;
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (photos.length + selectedFiles.length + acceptedFiles.length > 10) {
        const remaining = 10 - (photos.length + selectedFiles.length);
        toast.error(
          `You can only choose ${remaining} more ${remaining === 1 ? 'photo' : 'photos'} to upload`,
        );
        return;
      }
      setSelectedFiles(prev => [...prev, ...acceptedFiles]);
      const newPreviews = acceptedFiles.map(file => URL.createObjectURL(file));
      setFilePreviews(prev => [...prev, ...newPreviews]);
    },
    [photos.length, selectedFiles.length],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    disabled: isUploading || photos.length + selectedFiles.length >= 10,
  });

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(photos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setPhotos(items);
  };

  const [updateCarImages, { loading }] = useMutation(UPDATE_CAR_IMAGES, {
    refetchQueries: [
      {
        query: GET_CAR,
        variables: { id: carId },
      },
    ],
    onCompleted: () => {
      toast.success('Car images updated');
    },
    onError: error => {
      toast.error(`Error updating car images: ${error.message}`);
    },
  });
  const handleSavePressed = async () => {
    if (photos.length === 0) {
      toast.info('You cannot delete all photos, at least you should have one photo');
      return;
    }

    // Check if any changes were made
    const hasChanges = JSON.stringify(photos) !== JSON.stringify(passedInPhotos);

    if (!hasChanges) {
      toast.info('Please make changes to the photos before pressing Save Changes');
      return;
    }

    await updateCarImages({
      variables: {
        carId,
        updateImagesData: { imageUrls: photos },
      },
    });
  };

  const totalModalProgress =
    Object.keys(uploadProgress).length > 0
      ? Object.values(uploadProgress).reduce((a, b) => a + b, 0) /
        Object.keys(uploadProgress).length
      : 0;

  return (
    <Container>
      <h2>Car Photos</h2>
      <p>Drag to reorder photos. First photo will be the main photo.</p>

      {React.createElement(
        DragDropContext as any,
        { onDragEnd: handleDragEnd } as any,
        <StrictModeDroppable droppableId="photos" direction="horizontal">
          {(provided: DroppableProvided) => (
            <PhotoList {...provided.droppableProps} ref={provided.innerRef as any}>
              {photos.map((photo, index) => {
                const draggableId = `draggable-${photo}-${index}`;
                const DraggableComponent = Draggable as any;

                return (
                  <DraggableComponent key={draggableId} draggableId={draggableId} index={index}>
                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <PhotoItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'white',
                        }}
                      >
                        {index === 0 && <MainBadge>Main Photo</MainBadge>}
                        <PhotoThumb
                          src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${photo}`}
                          alt={`Car photo ${index + 1}`}
                        />
                        <PhotoInfo>
                          <PhotoLabel>Photo {index + 1}</PhotoLabel>
                          <DeleteButton
                            onClick={e => {
                              e.stopPropagation();
                              const newPhotos = [...photos];
                              newPhotos.splice(index, 1);
                              setPhotos(newPhotos);
                              toast.info(
                                'Photo removed from the list. Remember to press "Save Changes" to persist.',
                              );
                            }}
                          >
                            Delete
                          </DeleteButton>
                        </PhotoInfo>
                      </PhotoItem>
                    )}
                  </DraggableComponent>
                );
              })}
              {photos.length < 10 && (
                <AddPhotosCard onClick={() => setIsModalOpen(true)} type="button">
                  <PlusIcon>+</PlusIcon>
                  <AddText>Add Photos</AddText>
                </AddPhotosCard>
              )}
              {provided.placeholder as any}
            </PhotoList>
          )}
        </StrictModeDroppable>,
      )}
      <SaveButtonContainer>
        <Button
          text="Save Changes"
          onClick={handleSavePressed}
          isDisabled={loading}
          isLoading={loading}
        />
      </SaveButtonContainer>

      <Modal isOpen={isModalOpen} onClose={() => handleCloseModal()} modalWidth="600px">
        <ModalContentWrapper>
          <h3>Add New Photos</h3>
          <p>You can add up to {10 - photos.length} more photos.</p>

          <DropzoneContainer {...getRootProps({ isDragActive })} isDragActive={isDragActive}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the files here...</p>
            ) : (
              <p>Drag 'n' drop photos here, or click to select</p>
            )}
          </DropzoneContainer>

          {filePreviews.length > 0 && (
            <PreviewGrid>
              {filePreviews.map((preview, index) => (
                <PreviewCard key={index}>
                  <img src={preview} alt="preview" />
                </PreviewCard>
              ))}
            </PreviewGrid>
          )}

          {isUploading && (
            <div>
              <p>Uploading... {Math.round(totalModalProgress)}%</p>
              <ProgressBar progress={totalModalProgress} />
            </div>
          )}

          <ModalFooter>
            <Button
              text="Cancel"
              onClick={() => setIsModalOpen(false)}
              theme="outlined"
              isDisabled={isUploading}
            />
            <Button
              text="Save & Close"
              onClick={handleModalSave}
              isDisabled={isUploading || selectedFiles.length === 0}
              isLoading={isUploading}
            />
          </ModalFooter>
        </ModalContentWrapper>
      </Modal>
    </Container>
  );
};

export default EditCarPhotos;
